import { z } from "zod";

/**
 * Sports Park stadiums (ADR-0044): five venues where fans WATCH live AI games. One game at a time per stadium, all day:
 * a pre-game countdown (predictions open), the game (about 12 minutes with a sped-up game clock), the final whistle
 * (predictions settled), then the next pre-game. The home team is always Townsy's; the away team has a random name.
 *
 * The server decides everything: the teams, every play and the score come from a server-side seeded random source that
 * is never sent to a browser. The browser sends intents only: `cheer`, `predict`, `sit`, `stand`. Predictions use play
 * chips (ADR-0024, the stack shared with the casino and Townsy Downs): never money, coins or Townsy Credit.
 */
export const ARENA_KINDS = ["football_stadium", "soccer_stadium", "baseball_stadium", "basketball_arena", "hockey_arena"] as const;
export const ArenaKind = z.enum(ARENA_KINDS);
export type ArenaKind = z.infer<typeof ArenaKind>;

export type ArenaSport = "football" | "soccer" | "baseball" | "basketball" | "hockey";

/** The sport played in each stadium. */
export const ARENA_SPORT: Readonly<Record<ArenaKind, ArenaSport>> = {
  football_stadium: "football",
  soccer_stadium: "soccer",
  baseball_stadium: "baseball",
  basketball_arena: "basketball",
  hockey_arena: "hockey",
};

export function isArenaKind(kind: unknown): kind is ArenaKind {
  return typeof kind === "string" && (ARENA_KINDS as readonly string[]).includes(kind);
}

/** A team on the scoreboard. Colours are "#RRGGBB" (shirt / trim). */
export interface ArenaTeam {
  name: string;
  /** Three letters for the scoreboard ("TTN"). */
  short: string;
  primary: string;
  secondary: string;
}

/** The Townsy home team of each sport (ADR-0044 decision 2). */
export const ARENA_HOME_TEAMS: Readonly<Record<ArenaSport, ArenaTeam>> = {
  football: { name: "Townsy Titans", short: "TTN", primary: "#1F3A8A", secondary: "#F2B92C" },
  soccer: { name: "Townsy FC", short: "TFC", primary: "#1E8A4C", secondary: "#F4F4F0" },
  baseball: { name: "Townsy Sluggers", short: "TSL", primary: "#B8232B", secondary: "#F4ECDA" },
  basketball: { name: "Townsy Hoopers", short: "THP", primary: "#F07C1E", secondary: "#2B1B4A" },
  hockey: { name: "Townsy Blizzard", short: "TBZ", primary: "#2D8CD6", secondary: "#F4F8FC" },
};

/** Fans per stadium room (ADR-0044 decision 4; a per-venue setting, see VENUE_ROOM_CAPACITY). */
export const ARENA_ROOM_CAPACITY = 50;

/**
 * The schedule (ADR-0044 decision 3). The break between two games is `finalMs + pregameMs` (3 minutes): the final
 * score first, then the next game's countdown with predictions open. A stadium that just opened starts with a shorter
 * countdown so the first fan in does not wait three minutes.
 */
export const ARENA_TIMING = { pregameMs: 165_000, openingPregameMs: 60_000, finalMs: 15_000 } as const;
/** About this many real minutes of play per game (the game clock runs faster; baseball has no clock). */
export const ARENA_GAME_MINUTES = 12;
/** Shared state is broadcast about this often while a game is on (and on every change otherwise). */
export const ARENA_STATE_HZ = 10;

/** Prediction stakes (play chips). */
export const ARENA_STAKES = [10, 25, 50, 100] as const;
export type ArenaStake = (typeof ARENA_STAKES)[number];
/** Fixed payouts: a correct winner returns 2x the stake, a correct draw (soccer) 3x. A wrong pick returns nothing. */
export const ARENA_PAYOUT_MULTIPLIER = { winner: 2, draw: 3 } as const;
/** A fan may cheer at most once in this long (the server refuses faster cheers). */
export const ARENA_CHEER_COOLDOWN_MS = 2000;

export type ArenaSide = "home" | "away";
export type ArenaPick = "home" | "away" | "draw";

export const ArenaAction = z.discriminatedUnion("type", [
  /** Cheer for a team: your avatar celebrates and the crowd meter rises (one cheer per ARENA_CHEER_COOLDOWN_MS). */
  z.object({ type: z.literal("cheer"), team: z.enum(["home", "away"]) }).strict(),
  /** Predict the result of the next game (pre-game countdown only; one per fan per game; draw: soccer only). */
  z
    .object({
      type: z.literal("predict"),
      pick: z.enum(["home", "away", "draw"]),
      stake: z.union([z.literal(10), z.literal(25), z.literal(50), z.literal(100)]),
    })
    .strict(),
  /** Take the free seat nearest to you in the home or the away section. */
  z.object({ type: z.literal("sit"), section: z.enum(["home", "away"]) }).strict(),
  /** Stand up from your seat (you are back where you stood). */
  z.object({ type: z.literal("stand") }).strict(),
]);
export type ArenaAction = z.infer<typeof ArenaAction>;

export type ArenaPhase = "pregame" | "live" | "final";

/** Big moments get a scoreboard flash and a crowd roar for everyone (ADR-0044 decision 4). */
export type ArenaMomentKind = "touchdown" | "field_goal" | "goal" | "home_run" | "grand_slam" | "three_pointer" | "dunk" | "shootout_goal" | "final";

export interface ArenaMoment {
  /** Increases with every moment in this room (the client flashes once per id). */
  id: number;
  kind: ArenaMomentKind;
  /** 0 = home, 1 = away, null = both (the final whistle). */
  team: 0 | 1 | null;
  text: string;
  /** Server ms when it happened. */
  at: number;
}

/**
 * The scoreboard (what the big screen and the overlay show). Every sport has a score and a period; clocked sports
 * have a clock; baseball has the inning half, outs, bases and the count instead.
 */
export interface ArenaBoard {
  /** [home, away]. Hockey: the shootout winner's goal is included in the final score (as the NHL counts it). */
  score: [number, number];
  /** 1-based: quarter, half, period or inning (overtime and shootout continue the count). */
  period: number;
  /** "Q2", "2nd half", "P3", "OT", "2OT", "SO", "Top 7th", "Bot 9th", "HT", "INT", "FINAL". */
  periodLabel: string;
  /** Seconds shown on the game clock (football, basketball, hockey count down; soccer counts up). Null: baseball. */
  clock: number | null;
  clockRunning: boolean;
  /** Who has the ball / puck (0 = home, 1 = away), null when nobody does. */
  possession: 0 | 1 | null;
  /** American football: down 1-4, yards to go, the ball on this yard line ("TTN 35"). */
  football?: { down: 1 | 2 | 3 | 4; toGo: number; ballOn: string; goalToGo: boolean } | null;
  /** Baseball: top (away bats) or bottom (home bats), outs 0-2, bases [1st, 2nd, 3rd], the count. */
  baseball?: { half: "top" | "bottom"; outs: 0 | 1 | 2; bases: [boolean, boolean, boolean]; balls: 0 | 1 | 2 | 3; strikes: 0 | 1 | 2 } | null;
  /** Hockey shootout: goals per team so far and the round. */
  shootout?: { home: number; away: number; round: number } | null;
  /** A short line for the ticker ("First down", "Strike two", "Save!"), "" when nothing new. */
  status: string;
}

export interface ArenaResult {
  gameNo: number;
  home: ArenaTeam;
  away: ArenaTeam;
  score: [number, number];
  /** "home", "away" or "draw" (soccer only). */
  outcome: ArenaPick;
  /** "", "OT", "2OT", "SO", "F/10" (extra innings)... */
  suffix: string;
}

/** Positions of one game's players: index i keeps the same player for the whole game. */
export interface ArenaPlayers {
  /** Team of each player (0 = home, 1 = away). */
  team: Array<0 | 1>;
  /** Shirt number of each player. */
  num: number[];
  /** Role letter for the renderer ("G" goalkeeper / goalie, "P" pitcher, "B" batter, "R" runner, "" other). */
  role: string[];
}

/** `venue.state` of a stadium (to everyone: about 10 times a second while a game is on, and on every change). */
export interface ArenaState {
  sport: ArenaSport;
  /** Increases with every game in this room. */
  gameNo: number;
  phase: ArenaPhase;
  /** Milliseconds left in the pre-game or final phase when this was sent (0 while live). */
  endsIn: number;
  /** Milliseconds until the next kickoff (pre-game and final; 0 while live). */
  kickoffIn: number;
  home: ArenaTeam;
  away: ArenaTeam;
  board: ArenaBoard;
  /** Server sim clock (ms since kickoff) the positions below are at (0 before kickoff). */
  t: number;
  players: ArenaPlayers;
  /** Flat positions, 4 numbers per player: x and z (centimetres, room coordinates), facing (centiradians), animation. */
  pos: number[];
  /** Ball or puck: x, y, z (centimetres), or null when out of play. */
  ball: [number, number, number] | null;
  /** Crowd noise 0-100 for each side (cheers raise it, it fades back). */
  crowd: { home: number; away: number };
  /**
   * Cheers in the last moments: who (actor id), for which side (0 = home, 1 = away), when (server ms) and where the fan
   * was (metres, room coordinates), so every browser shows them celebrating.
   */
  cheers: Array<{ a: string; team: 0 | 1; at: number; x: number; z: number }>;
  /** The latest big moment (flash it once per id while recent), or null. */
  moment: ArenaMoment | null;
  /** The last few plays, newest last. */
  feed: string[];
  /** The previous game's result (shown during the break), or null. */
  last: ArenaResult | null;
  /** Predictions are being taken (pre-game countdown, and play-chip games are switched on). */
  predictionsOpen: boolean;
  /** Occupied fan seats: [seat index, actor id]. */
  seats: Array<[number, string]>;
  /** Server time of this state (ms), for the cheer and moment ages. */
  now: number;
}

/**
 * Player animation codes in `ArenaState.pos`. `action` is a pass, a throw, a kick or a jump shot; `dunk` a leap that
 * jams the ball through the rim; `layup` a running one-handed leap at the rim; `shot` a hockey shot (the stick wound
 * back and swung through). A client that does not know a code draws the player standing or running.
 */
export const ARENA_ANIM = { idle: 0, run: 1, action: 2, celebrate: 3, down: 4, hidden: 5, dunk: 6, layup: 7, shot: 8 } as const;

export interface ArenaPrediction {
  gameNo: number;
  pick: ArenaPick;
  stake: number;
  /** Chips returned if the pick is right (2x, or 3x for a draw). */
  payout: number;
}

/** `venue.private` of a stadium (to one fan: their prediction, their last result, their seat). */
export interface ArenaPrivate {
  gameNo: number;
  /** Your prediction on the coming (or running) game, or null. */
  prediction: ArenaPrediction | null;
  /** Your result on the last game you predicted (null until then). */
  last: { gameNo: number; pick: ArenaPick; stake: number; won: number; outcome: ArenaPick } | null;
  /** Your fan seat index, or null when standing. */
  seat: number | null;
  section: ArenaSide | null;
  /** Milliseconds until you may cheer again (0 = now). */
  cheerIn: number;
}

/** Chips returned for a prediction once the game is over: 2x for a correct winner, 3x for a correct draw, else 0. */
export function arenaPayout(pick: ArenaPick, stake: number, outcome: ArenaPick): number {
  if (!Number.isSafeInteger(stake) || stake <= 0 || pick !== outcome) return 0;
  return stake * (outcome === "draw" ? ARENA_PAYOUT_MULTIPLIER.draw : ARENA_PAYOUT_MULTIPLIER.winner);
}

/** The picks a sport allows (a draw is possible in soccer only). */
export function arenaPicks(sport: ArenaSport): readonly ArenaPick[] {
  return sport === "soccer" ? ["home", "away", "draw"] : ["home", "away"];
}

export const ARENA_PREDICTION_NOTICE =
  "Predictions use play chips: just for fun, no value. They can't be bought, cashed out, transferred or turned into coins or Townsy Credit. It is the same stack as the casino and Townsy Downs, kept for your next visit; if you have fewer than 100 when you walk in, you get 100. Predicting needs an account (18+).";
